<template>
  <div class="hendricks-container relative min-h-screen pb-20 overflow-hidden">
    <!-- Background Elements -->
    <div class="absolute inset-0 bg-pattern opacity-10"></div>
    <div class="absolute top-0 left-0 w-1/3 h-1/2 bg-gradient-radial from-primary/20 to-transparent opacity-20 blur-3xl"></div>
    <div class="absolute bottom-0 right-0 w-1/2 h-1/3 bg-gradient-radial from-secondary/10 to-transparent opacity-20 blur-3xl"></div>
    
    <!-- Decorative Elements -->
    <div class="absolute left-0 top-1/4 h-[60%] w-1 bg-gradient-to-b from-transparent via-primary/30 to-transparent"></div>
    <div class="absolute right-0 top-1/3 h-[40%] w-1 bg-gradient-to-b from-transparent via-secondary/30 to-transparent"></div>
    <div class="steel-accent top-accent"></div>
    <div class="steel-accent bottom-accent"></div>
    
    <!-- Floating Image Elements -->
    <div class="floating-images">
      <div 
        v-for="(image, index) in floatingImages" 
        :key="index"
        class="floating-image"
        :class="`image-${index + 1}`"
        :style="{ backgroundImage: `url(${image.src})` }"
        @mousemove="handleFloatingImageTilt($event, index)"
        @mouseleave="resetFloatingImageTilt(index)"
      >
        <div class="image-border"></div>
      </div>
    </div>
    <!-- Section Separator -->
    <div class="section-separator">
      <div class="separator-content">
        <div class="separator-line left"></div>
        <div class="separator-emblem">
          <div class="emblem-outer"></div>
          <div class="emblem-inner">
            <div class="emblem-icon"></div>
          </div>
        </div>
        <div class="separator-line right"></div>
      </div>
      <div class="separator-triangles">
        <div class="triangle-left"></div>
        <div class="triangle-right"></div>
      </div>
    </div>

    <!-- Steel Accent Elements -->
    <div class="steel-accent left"></div>
    <div class="steel-accent right"></div>
    <div class="steel-accent top"></div>
    <div class="steel-accent bottom"></div>

    <!-- Header Section with Parallax -->
    <div 
      class="hendricks-header relative z-10 pt-[15vh] px-6 text-center mb-12 opacity-0 transform translate-y-8"
      ref="header"
      @mousemove="handleParallax($event, 'header')"
      :class="{'fade-in': isInView.header}"
    >
      <div class="header-accent-line"></div>
      <div class="header-accent-circle"></div>
      
      <h2 class="text-secondary/70 text-2xl md:text-3xl mb-2 font-light tracking-wide">{{ firstLineText }}</h2>
      <h1 class="text-white text-4xl md:text-6xl font-bold relative inline-block">
        <span class="relative z-10">{{ secondLineText }}</span>
        <span class="absolute bottom-0 left-0 w-full h-1 bg-primary transform scale-x-0 origin-left transition-transform duration-1000"
              :class="{'scale-x-100': isInView.header}"></span>
      </h1>
      <div class="mt-8 max-w-4xl mx-auto text-center">
        <p class="text-white/70 text-lg md:text-xl leading-relaxed">
          Supporting American manufacturing excellence through strategic investment and unwavering commitment to quality craftsmanship.
        </p>
      </div>
    </div>    
    <!-- Content Container -->
    <div class="content-wrapper relative max-w-7xl mx-auto px-4 mt-20">
      <!-- Main Content Grid -->
      <div class="content-grid">
        <!-- Left Content Section -->
        <div class="content-section left-section" ref="section1" :class="{'fade-in': isInView.section1}">
          <div class="content-card glassmorphism">
            <div class="card-header">
              <h3 class="card-title">Hendricks Holding Company, Inc.</h3>
              <div class="accent-line"></div>
            </div>
            
            <p class="text-secondary/90 text-xl md:text-2xl font-light leading-tight mb-6">
              A private holding company that invests in and oversees diverse, financially strong, and sustainable companies across North America.
            </p>
            
            <p class="text-white/80 text-base md:text-lg leading-relaxed mb-4">
              Founded by Ken and Diane Hendricks, Hendricks Holding Company has built a remarkable legacy of supporting American businesses and manufacturing excellence. Based in Beloit, Wisconsin, HHC operates with a clear mission: to provide companies with the resources and support they need to reach their business goals and deliver best-in-class services to their respective industries.
            </p>
            
            <p class="text-white/80 text-base md:text-lg leading-relaxed mb-6">
              When STE faced challenging times in 2006, Hendricks Holding Company stepped in to rescue the company from receivership. As explained by the Hendricks family, "It employed a lot of hard-working people, and they didn't want those jobs to go away. So they chose to find a way to make STE work."
            </p>

            <!-- Hendricks Headquarters Image -->
            <div class="actual-image glassmorphism mb-6" ref="imagePlaceholder1">
              <img 
                src="/img/hendricks/headquarters.jpg" 
                alt="Hendricks Holding Company Headquarters, Beloit, Wisconsin"
                class="w-full h-full object-cover rounded-lg"
                loading="lazy"
              />
              <div class="image-overlay">
                <p class="overlay-text">Hendricks Holding Company Headquarters</p>
                <p class="overlay-subtext">Beloit, Wisconsin</p>
              </div>
            </div>
          </div>
        </div>        
        <!-- Right Content Section -->
        <div class="content-section right-section" ref="section2" :class="{'fade-in': isInView.section2}">
          <div class="content-card glassmorphism">
            <div class="card-header">
              <h3 class="card-title">Supporting STE's Growth</h3>
              <div class="accent-line"></div>
            </div>
            
            <p class="text-secondary/90 text-xl md:text-2xl font-light leading-tight mb-6">
              The investment and support from Hendricks Holding has enabled STE to grow its brand, expand product offerings, and build state-of-the-art manufacturing facilities.
            </p>
            
            <p class="text-white/80 text-base md:text-lg leading-relaxed mb-4">
              Under the Hendricks umbrella, STE has flourished with access to resources that support innovation, quality improvement, and strategic growth. This partnership has allowed STE to maintain its commitment to American manufacturing while expanding capabilities and market reach.
            </p>

            <!-- Core Values Section -->
            <div class="values-section mb-6">
              <h4 class="values-title text-white font-semibold text-lg mb-3">Shared Core Values</h4>
              <div class="values-grid">
                <div class="value-item">
                  <div class="value-icon">
                    <font-awesome-icon :icon="['fas', 'handshake']" />
                  </div>
                  <span>Respect</span>
                </div>
                <div class="value-item">
                  <div class="value-icon">
                    <font-awesome-icon :icon="['fas', 'rocket']" />
                  </div>
                  <span>Opportunity</span>
                </div>
                <div class="value-item">
                  <div class="value-icon">
                    <font-awesome-icon :icon="['fas', 'users']" />
                  </div>
                  <span>Family</span>
                </div>
                <div class="value-item">
                  <div class="value-icon">
                    <font-awesome-icon :icon="['fas', 'flag-usa']" />
                  </div>
                  <span>American Pride</span>
                </div>
              </div>
            </div>
            <!-- Hendricks Building Image -->
            <div class="actual-image glassmorphism mb-6" ref="imagePlaceholder2">
              <img 
                src="/img/hendricks/hendricks-building.jpg" 
                alt="Hendricks Manufacturing Excellence and Diverse Portfolio"
                class="w-full h-full object-cover rounded-lg"
                loading="lazy"
              />
              <div class="image-overlay">
                <p class="overlay-text">Manufacturing Excellence</p>
                <p class="overlay-subtext">Diverse Portfolio Companies</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Call to Action Section -->
      <div class="cta-section" ref="ctaSection" :class="{'fade-in': isInView.ctaSection}">
        <div class="cta-card glassmorphism">
          <div class="cta-content">
            <h3 class="cta-title">Learn More About Hendricks Holding</h3>
            <p class="cta-description">
              Discover more about our parent company's diverse portfolio and commitment to supporting exceptional businesses across multiple industries.
            </p>
            
            <!-- ABC Supply Portfolio Image -->
            <div class="actual-image glassmorphism mb-6" ref="imagePlaceholder3">
              <img 
                src="/img/hendricks/abc-supply.jpg" 
                alt="ABC Supply - Leading Hendricks Portfolio Company"
                class="w-full h-full object-cover rounded-lg"
                loading="lazy"
              />
              <div class="image-overlay">
                <p class="overlay-text">Portfolio Companies</p>
                <p class="overlay-subtext">Transportation • Manufacturing • Real Estate</p>
              </div>
            </div>
            
            <a 
              href="https://www.hendricksholding.com/"
              target="_blank"
              rel="noopener noreferrer"
              class="hendricks-button group"
              @mouseenter="btnHovered = true"
              @mouseleave="btnHovered = false"
            >
              <span class="btn-text" :class="{'btn-text-animate': btnHovered}">
                Visit Hendricks Holding
              </span>
              <span class="btn-icon" :class="{'btn-icon-animate': btnHovered}">
                <font-awesome-icon :icon="['fas', 'external-link-alt']" />
              </span>
              <div class="btn-bg" :class="{'btn-bg-animate': btnHovered}"></div>
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'HendricksHoldings',
  data() {
    return {
      firstLineText: 'Backed by excellence',
      secondLineText: 'Hendricks Holding Company',
      observers: [],
      isInView: {
        header: false,
        section1: false,
        section2: false,
        ctaSection: false
      },
      btnHovered: false,
      floatingImages: [
        { src: '/img/hendricks/headquarters-sub.jpg', tilt: { x: 0, y: 0 } },
        { src: '/img/hendricks/abclogo.png', tilt: { x: 0, y: 0 } }
      ]
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.setupIntersectionObservers();
      
      // Add initial entrance animation
      setTimeout(() => {
        this.isInView.header = true;
      }, 500);
    });
  },
  beforeUnmount() {
    this.observers.forEach(observer => observer.disconnect());
  },  methods: {
    setupIntersectionObservers() {
      const sections = [
        { ref: 'header', threshold: 0.3 },
        { ref: 'section1', threshold: 0.2 },
        { ref: 'section2', threshold: 0.2 },
        { ref: 'ctaSection', threshold: 0.2 }
      ];
      
      sections.forEach(section => {
        const element = this.$refs[section.ref];
        if (!element) return;
        
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              this.isInView[section.ref] = true;
            } else if (section.ref !== 'header') {
              this.isInView[section.ref] = false;
            }
          });
        }, { 
          threshold: section.threshold,
          rootMargin: '0px 0px -100px 0px'
        });
        
        observer.observe(element);
        this.observers.push(observer);
      });
    },

    handleParallax(e, elementType) {
      const element = this.$refs[elementType];
      if (!element) return;
      
      const rect = element.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      
      const distanceX = (e.clientX - centerX) / (rect.width / 2);
      const distanceY = (e.clientY - centerY) / (rect.height / 2);
      
      if (elementType === 'header') {
        element.style.transform = `translate(${distanceX * -10}px, ${distanceY * -10}px)`;
      }
    },    
    handleFloatingImageTilt(e, index) {
      const target = e.currentTarget;
      const rect = target.getBoundingClientRect();
      
      const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const y = ((e.clientY - rect.top) / rect.height) * 2 - 1;
      
      const tiltX = y * -5;
      const tiltY = x * 5;
      
      this.floatingImages[index].tilt = { x: tiltX, y: tiltY };
      
      target.style.transform = `perspective(1000px) rotateX(${tiltX}deg) rotateY(${tiltY}deg) scale3d(1.05, 1.05, 1.05)`;
      
      const image = target.querySelector('.image-border');
      if (image) {
        image.style.transform = `translate(${x * -5}px, ${y * -5}px)`;
      }
    },
    
    resetFloatingImageTilt(index) {
      const image = document.querySelector(`.image-${index + 1}`);
      if (image) {
        image.style.transform = '';
        
        const border = image.querySelector('.image-border');
        if (border) {
          border.style.transform = '';
        }
      }
      
      this.floatingImages[index].tilt = { x: 0, y: 0 };
    }
  }
};
</script>
<style scoped>
/* Base Styles */
.hendricks-container {
  color: white;
  will-change: transform;
}

.bg-pattern {
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}

/* Floating Images Styling */
.floating-images {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  pointer-events: none;
}

.floating-image {
  position: absolute;
  width: 320px;
  height: 240px;
  background-size: cover;
  background-position: center;
  border-radius: 12px;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.6);
  opacity: 0.7;
  transition: all 0.3s ease;
  cursor: pointer;
  pointer-events: auto;
  transform-style: preserve-3d;
  overflow: hidden;
}

.floating-image:hover {
  opacity: 1;
  z-index: 20;
  transform: scale(1.05);
}

.image-border {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 3px solid var(--color-primary);
  border-radius: 12px;
  box-sizing: border-box;
  transition: transform 0.3s ease;
}

/* Position floating images */
.image-1 {
  top: 20%;
  left: 50px;
  transform: rotate(-3deg);
  z-index: 6;
}

.image-2 {
  top: 60%;
  right: 80px;
  transform: rotate(4deg);
  z-index: 5;
}

/* Steel Accent Elements */
.steel-accent {
  position: absolute;
  opacity: 0.1;
  z-index: 1;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.5), rgba(200, 200, 200, 0.2));
}

.top-accent {
  top: 0;
  right: 10%;
  width: 30%;
  height: 2px;
  transform: skewX(-30deg);
}

.bottom-accent {
  bottom: 0;
  left: 10%;
  width: 30%;
  height: 2px;
  transform: skewX(30deg);
}

.steel-accent.left {
  top: 30%;
  left: 0;
  width: 40px;
  height: 40%;
  clip-path: polygon(0 0, 100% 20%, 100% 80%, 0 100%);
}
.steel-accent.right {
  top: 20%;
  right: 0;
  width: 40px;
  height: 60%;
  clip-path: polygon(0 20%, 100% 0, 100% 100%, 0 80%);
}

.steel-accent.top {
  top: 0;
  left: 20%;
  width: 60%;
  height: 30px;
  clip-path: polygon(0 0, 100% 0, 80% 100%, 20% 100%);
}

.steel-accent.bottom {
  bottom: 0;
  left: 30%;
  width: 40%;
  height: 25px;
  clip-path: polygon(20% 0, 80% 0, 100% 100%, 0 100%);
}

/* Header Styles */
.hendricks-header {
  transition: opacity 1s ease, transform 1s ease;
}

.header-accent-line {
  width: 120px;
  height: 3px;
  background: linear-gradient(90deg, var(--color-primary), transparent);
  margin: 0 auto 1.5rem;
}

.header-accent-circle {
  position: absolute;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  border: 1px solid rgba(143, 0, 0, 0.1);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  opacity: 0.3;
  animation: rotate 30s linear infinite;
  pointer-events: none;
}

.header-accent-circle::before {
  content: '';
  position: absolute;
  width: 250px;
  height: 250px;
  border-radius: 50%;
  border: 1px dashed rgba(143, 0, 0, 0.05);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation: rotate 40s linear infinite reverse;
}

/* Content Styles */
.content-wrapper {
  position: relative;
}

.content-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  margin-bottom: 80px;
}

.content-section {
  opacity: 0;
  transform: translateY(50px);
  transition: all 1s cubic-bezier(0.23, 1, 0.32, 1);
}

.content-section.fade-in {
  opacity: 1;
  transform: translateY(0);
}

.content-card {
  padding: 40px;
  border-radius: 12px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
}

.glassmorphism {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.glassmorphism:hover {
  background: rgba(255, 255, 255, 0.05);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

.card-header {
  margin-bottom: 2rem;
}

.card-title {
  font-size: 1.875rem;
  font-weight: bold;
  margin-bottom: 0.75rem;
  color: white;
}

.accent-line {
  width: 80px;
  height: 3px;
  background: var(--color-primary);
  transition: width 0.3s ease;
}

.content-card:hover .accent-line {
  width: 120px;
}

/* Values Section */
.values-section {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 1.5rem;
}

.values-title {
  color: #b7b9ba;
}

.values-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}

.value-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: all 0.3s ease;
}

.value-item:hover {
  background: rgba(255, 255, 255, 0.05);
  transform: translateY(-2px);
}

.value-icon {
  color: var(--color-primary);
  font-size: 1.25rem;
  min-width: 20px;
}

/* Actual Images */
.actual-image {
  height: 200px;
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.actual-image:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.3);
}

.actual-image img {
  border-radius: 8px;
  transition: transform 0.3s ease;
}

.actual-image:hover img {
  transform: scale(1.05);
}

.image-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.8), transparent);
  padding: 16px;
  color: white;
  transition: all 0.3s ease;
}

.overlay-text {
  font-size: 1.125rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.overlay-subtext {
  font-size: 0.875rem;
  opacity: 0.8;
}

/* Image Placeholders */
.image-placeholder {
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px dashed rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  transition: all 0.3s ease;
}

.image-placeholder:hover {
  border-color: rgba(255, 255, 255, 0.4);
  background: rgba(255, 255, 255, 0.02);
}

.placeholder-content {
  text-align: center;
  color: rgba(255, 255, 255, 0.6);
}

.placeholder-icon {
  font-size: 3rem;
  color: var(--color-primary);
  margin-bottom: 1rem;
}
.placeholder-text {
  font-size: 1.125rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.placeholder-subtext {
  font-size: 0.875rem;
  opacity: 0.7;
}

/* CTA Section */
.cta-section {
  opacity: 0;
  transform: translateY(50px);
  transition: all 1s cubic-bezier(0.23, 1, 0.32, 1);
}

.cta-section.fade-in {
  opacity: 1;
  transform: translateY(0);
}

.cta-card {
  padding: 60px 40px;
  text-align: center;
}

.cta-title {
  font-size: 2.25rem;
  font-weight: bold;
  margin-bottom: 1.5rem;
  color: white;
}

.cta-description {
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 3rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}
/* Button Styling */
.hendricks-button {
  position: relative;
  padding: 16px 32px;
  border-radius: 8px;
  font-weight: bold;
  color: white;
  overflow: hidden;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 12px;
  background: transparent;
  border: 2px solid rgba(143, 0, 0, 0.5);
  transition: all 0.3s ease;
  font-size: 1.125rem;
}

.btn-text {
  position: relative;
  z-index: 2;
  transition: transform 0.3s ease;
}

.btn-text-animate {
  transform: translateX(-5px);
}

.btn-icon {
  position: relative;
  z-index: 2;
  opacity: 0.8;
  transition: all 0.3s ease;
  font-size: 1rem;
}

.btn-icon-animate {
  transform: translateX(5px);
  opacity: 1;
}

.btn-bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(90deg, var(--color-primary), #690000);
  z-index: 1;
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.3s ease;
}

.btn-bg-animate {
  transform: scaleX(1);
}
.hendricks-button:hover {
  box-shadow: 0 8px 25px rgba(143, 0, 0, 0.4);
  border-color: var(--color-primary);
  transform: translateY(-2px);
}

/* Section Separator */
.section-separator {
  position: absolute;
  bottom: -100px;
  left: 50%;
  transform: translateX(-50%);
  width: 300px;
  height: 60px;
  z-index: 10;
}

.separator-content {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.separator-line {
  height: 2px;
  background: linear-gradient(90deg, transparent, rgba(143, 0, 0, 0.5), transparent);
  flex: 1;
}

.separator-emblem {
  width: 40px;
  height: 40px;
  position: relative;
  margin: 0 20px;
}

.emblem-outer {
  width: 100%;
  height: 100%;
  border: 2px solid rgba(143, 0, 0, 0.3);
  border-radius: 50%;
  position: absolute;
  animation: rotate 20s linear infinite;
}

.emblem-inner {
  width: 24px;
  height: 24px;
  background: rgba(143, 0, 0, 0.2);
  border-radius: 50%;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
}
.emblem-icon::before {
  content: '◆';
  color: var(--color-primary);
  font-size: 12px;
}

/* Animations */
.fade-in {
  opacity: 1 !important;
  transform: translateY(0) !important;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Responsive Styles */
@media (max-width: 1024px) {
  .content-grid {
    grid-template-columns: 1fr;
    gap: 40px;
  }
  
  .floating-images {
    display: none;
  }
  
  .values-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .hendricks-header {
    padding-top: 10vh;
  }
  
  .card-title {
    font-size: 1.5rem;
  }
  
  .cta-title {
    font-size: 1.875rem;
  }
  
  .content-card,
  .cta-card {
    padding: 30px 20px;
  }
  
  .image-placeholder {
    height: 150px;
  }
  
  .placeholder-icon {
    font-size: 2rem;
  }
}
@media (max-width: 640px) {
  .hendricks-header {
    padding-top: 5vh;
  }
  
  .hendricks-button {
    padding: 12px 24px;
    font-size: 1rem;
  }
  
  .cta-description {
    font-size: 1.125rem;
  }
}

/* CSS Variables for colors */
:root {
  --color-primary: #a22a21;
  --color-secondary: #b7b9ba;
}
</style>